import React from "react";
import {Heading} from "@/components/ui/heading";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion"
import parse from 'html-react-parser';

const Faq = ({page}) => {

    const homeFaq = [
        {
            sectionTitle: 'General Information',
            questions: [
                {
                    question: 'What is the Virtual Assistant?',
                    answer: 'The Virtual Assistant is a tool that analyzes financial instruments to identify and suggest entry and exit points for positions.'
                },
                {
                    question: 'How does the Virtual Assistant work?',
                    answer: 'This tool compares multiple charts simultaneously to find the optimal entry points for positions.'
                },
                {
                    question: 'What are the differences between the Virtual Assistant packages?',
                    answer: 'All packages provide the same version of the Virtual Assistant. The only difference lies in the number of technical analyses generated using the indicator\'s settings button.'
                }
            ],
        },
        {
            sectionTitle: 'Settings and Usage',
            questions: [
                {
                    question: 'How can I configure the indicator, and where can I find information about it?',
                    answer: 'Details are provided in the instructional video available here: LINK.'
                },
                {
                    question: 'How should I set up the indicator?',
                    answer: 'Settings should be tailored to your trading style and the specific time interval being analyzed.'
                },
                {
                    question: 'Do I need to reconfigure the Virtual Assistant for each open pair?',
                    answer: 'Yes, as each currency pair behaves differently, historical data should be reviewed to determine the best signals for each pair.'
                }
            ],
        },
        {
            sectionTitle: 'Signals and Notifications',
            questions: [
                {
                    question: 'What do the different symbols mean?',
                    answer: '<strong>Blue arrow below the chart</strong> – Indicates a proposed position for a price increase.<br/>' +
                        '<strong>Red arrow above the chart</strong> – Indicates a proposed position for a price decrease.'
                },
                {
                    question: 'What do the \'arrows\' and \'gray candles\' settings mean?',
                    answer: '<strong>Arrows</strong> indicate entry signals.<br/>' +
                        '<strong>Gray candles</strong> mark the exit points for positions.'
                },
                {
                    question: 'Will entry notifications be sent to my phone?',
                    answer: 'Yes, notifications will be sent via push notifications to connected devices.'
                },
                {
                    question: 'Can I immediately take a position upon receiving a long or short signal without additional analysis?',
                    answer: 'No – it is recommended to ensure that at least two timeframes indicate the same trend. Additionally, technical analysis should be performed, and the price should align with the boundary values of the linear regression channel consistent with the trend.'
                }
            ],
        },
        {
            sectionTitle: 'Exiting Positions',
            questions: [
                {
                    question: 'When is it advisable to exit a position?',
                    answer: 'An open position should be closed when the upward or downward signal is interrupted, marked by the first gray candle on the chart. If the profit is satisfactory and aligns with your plan, it is advisable to secure the profit or a portion of it before the signal concludes.'
                },
            ],
        },
        {
            sectionTitle: 'Recommendations and Strategies',
            questions: [
                {
                    question: 'What are the recommended currency pairs?',
                    answer: 'Combinations of AUD, USD, CAD, EUR, GBP, JPY, NZD, CHF are recommended.'
                },
            ],
        },
        {
            sectionTitle: 'Payment Issues',
            questions: [
                {
                    question: 'What should I do if I made a payment for the indicator but didn’t include the order number in the title?',
                    answer: 'If the payment was made from your account, the payment will be verified using the name and surname. If the payment was made from someone else’s account, verification will be required. Please contact us at: <a href="mailto:support@lockedtrade.com">support@lockedtrade.com</a>.'
                },
            ],
        },
    ]

    const affiliateProgramFaq = [
        {
            sectionTitle: 'General Information',
            questions: [
                {
                    question: 'What is an affiliate program?',
                    answer: 'An affiliate program is a system that allows you to earn money by promoting our product or service. You receive a commission for each person who makes a purchase using your unique affiliate link.'
                },
                {
                    question: 'Who can join the affiliate program?',
                    answer: 'The affiliate program is open to everyone who wants to promote our products and services—both individuals and businesses. A bank account is required to receive commissions.'
                },
            ],
        },
        {
            sectionTitle: 'Registration and Account',
            questions: [
                {
                    question: 'How can I register for the affiliate program?',
                    answer: 'Registration is done through our website. Simply fill out the application form and accept the program\'s terms and conditions.'
                },
                {
                    question: 'Are there any requirements to join the program?',
                    answer: 'There are no specific requirements—just the willingness to promote our services. However, your registration may be reviewed by our team.'
                },
                {
                    question: 'How will I receive my affiliate link?',
                    answer: 'Once your registration is successful, your unique affiliate link will be made available in your user panel.'
                },
            ],
        },
        {
            sectionTitle: 'Earnings and Commissions',
            questions: [
                {
                    question: 'How much can I earn through the affiliate program?',
                    answer: 'Your earnings depend on the number of people who make purchases through your affiliate link. The commission amount may be a set percentage of the sale value or a fixed amount.'
                },
                {
                    question: 'When and how will I receive my commission?',
                    answer: 'Commissions are paid out monthly after meeting the minimum payout threshold. Payments are made to the bank account you provided.'
                },
                {
                    question: 'Is there a minimum payout threshold?',
                    answer: 'Yes, the minimum payout threshold is [amount], which ensures efficient management of the payout process.'
                },
            ],
        },
        {
            sectionTitle: 'Tracking and Statistics',
            questions: [
                {
                    question: 'How can I check my earnings and statistics?',
                    answer: 'In your user panel, you can monitor the number of clicks on your link, the number of transactions completed by your referrals, and the total commissions accrued.'
                },
                {
                    question: 'Can I track which transactions were made through my link?',
                    answer: 'Yes, detailed reports in your user panel allow you to track transactions associated with your affiliate link.'
                },
            ],
        },
        {
            sectionTitle: 'Promotion and Materials',
            questions: [
                {
                    question: 'Can I get promotional materials?',
                    answer: 'Yes, we provide promotional materials such as banners, graphics, and descriptions that you can use in your campaigns.'
                },
                {
                    question: 'Where can I promote my affiliate link?',
                    answer: 'You can promote your link in any legal space, such as blogs, websites, social media, or email campaigns.'
                },
                {
                    question: 'Are there rules for promotion?',
                    answer: 'Yes, the affiliate link must not be promoted in an illegal, misleading way or in a manner that infringes on the rights of third parties. Details are provided in the program\'s terms and conditions.'
                },
            ],
        },
        {
            sectionTitle: 'Issues and Support',
            questions: [
                {
                    question: 'What should I do if I encounter a problem with the affiliate program?',
                    answer: 'If you experience any issues, contact our support team at: <a href="mailto:support@lockedtrade.com">support@lockedtrade.com</a>.'
                },
                {
                    question: 'What if I haven\'t received my commission?',
                    answer: 'Ensure you’ve met the minimum payout threshold and that your bank details are correct. If the issue persists, contact our support team at <a href="mailto:support@lockedtrade.com">support@lockedtrade.com</a>.'
                },
            ],
        },
    ]

    const supportFaq = [
        {
            sectionTitle: '',
            questions: [
                {
                    question: '1. How can I register on the platform?',
                    answer: 'To register, simply provide your email address and password during the sign-up process, then confirm your email by clicking the activation link sent to your inbox.'
                },
                {
                    question: '2. Do I need to purchase access to use the AI Assistant?',
                    answer: 'Yes, the AI Assistant is available for users with an active subscription. You can purchase access after registering on the platform.'
                },
                {
                    question: '3. Can I test the AI Assistant before purchasing?',
                    answer: 'Depending on current promotions, a demo version may be available. Check the main website to see if a trial period is offered.'
                },
                {
                    question: '4. How long does it take to activate my account after purchase?',
                    answer: 'AI Assistant access is activated immediately after payment is processed. If you experience any delays, check your email inbox or contact support.'
                },
                {
                    question: '5. Can I use the AI Assistant on multiple devices?',
                    answer: 'Yes, you can log in from different devices, but you can only use the AI Assistant on one device at a time.'
                },
                {
                    question: '6. How can I adjust the AI Assistant settings to fit my strategy?',
                    answer: 'You can modify parameters such as signal length, risk levels, and technical indicators to tailor the AI Assistant to your trading strategy.'
                },
                {
                    question: '7. What indicators can I enable in the AI Assistant?',
                    answer: 'You can choose from indicators such as moving averages (EMA), linear regression channels, and other technical analysis tools to enhance your decision-making.'
                },
                {
                    question: '8. Does the AI Assistant work in real time?',
                    answer: 'Yes, the system continuously analyzes the market and generates signals in real time, adapting to current market conditions.'
                },
                {
                    question: '9. How often are trading signals generated?',
                    answer: 'Signals are generated based on your selected settings and market dynamics. You can adjust their frequency according to your preferences.'
                },
                {
                    question: '10. Can I disable or modify specific signals?',
                    answer: 'Yes, you have the option to filter signals and adjust them according to your needs, selecting only the ones that matter most to you.'
                },
                {
                    question: '11. How does the magic technical analysis button work?',
                    answer: 'The magic button automatically generates a chart analysis, filtering key levels and trends, allowing you to quickly identify the most important trading signals.'
                },
                {
                    question: '12. Can I draw support and resistance levels on charts?',
                    answer: 'Yes, drawing tools allow you to mark key levels, define price channels, and plan your entry and exit points.'
                },
                {
                    question: '13. What technical analysis tools are available in the AI Assistant?',
                    answer: 'You can use popular tools such as moving averages, support and resistance levels, regression channels, and automated key level marking.'
                },
                {
                    question: '14. Can I save my chart settings for future use?',
                    answer: 'Yes, your settings can be saved, so you don’t have to reconfigure them every time you log in.'
                },
                {
                    question: '15. How can I receive notifications about key signals?',
                    answer: 'Notifications can be activated in your account settings, ensuring you receive real-time alerts on the most important market signals.'
                },
            ],
        },
    ]

    return (
        <section id="section-faq" className="py-14">
            <div className="generic-container">
                <div style={{textAlign: 'center'}}>
                    {page !== "support" && (
                        <Heading level={2}>
                            Frequently Asked Questions
                        </Heading>
                    )}

                    <Accordion className={`pt-14`} type="single" collapsible>
                        {page === "home" && homeFaq.map((faqGroup, indexGroup) => (
                            <>
                                <p className="section-faq-group-title">{faqGroup.sectionTitle}</p>
                                {faqGroup.questions.map((faq, indexQuestion) => (
                                    <AccordionItem key={indexQuestion + 100 * indexGroup}
                                                   value={`item-${indexQuestion + 100 * indexGroup}`}>
                                        <AccordionTrigger
                                            className={`custom-accordion-title`}>{faq.question}</AccordionTrigger>
                                        <AccordionContent className={`custom-accordion-content`}>
                                            {parse(faq.answer)}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </>
                        ))}
                        {page === "affiliate-program" && affiliateProgramFaq.map((faqGroup, indexGroup) => (
                            <>
                                <p className="section-faq-group-title">{faqGroup.sectionTitle}</p>
                                {faqGroup.questions.map((faq, indexQuestion) => (
                                    <AccordionItem key={indexQuestion + 100 * indexGroup}
                                                   value={`item-${indexQuestion + 100 * indexGroup}`}>
                                        <AccordionTrigger
                                            className={`custom-accordion-title`}>{faq.question}</AccordionTrigger>
                                        <AccordionContent className={`custom-accordion-content`}>
                                            {parse(faq.answer)}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </>
                        ))}
                        {page === "support" && supportFaq.map((faqGroup, indexGroup) => (
                            <>
                                <p className="section-faq-group-title">{faqGroup.sectionTitle}</p>
                                {faqGroup.questions.map((faq, indexQuestion) => (
                                    <AccordionItem key={indexQuestion + 100 * indexGroup}
                                                   value={`item-${indexQuestion + 100 * indexGroup}`}>
                                        <AccordionTrigger
                                            className={`custom-accordion-title`}>{faq.question}</AccordionTrigger>
                                        <AccordionContent className={`custom-accordion-content`}>
                                            {parse(faq.answer)}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </>
                        ))}
                    </Accordion>


                </div>
            </div>
        </section>
    )
}

export default Faq;